import Joi from 'joi';
import { HttpError } from '../middleware';
import { ErrorCode } from '../types';

/**
 * Options for schema validation
 */
export interface SchemaValidationOptions {
  abortEarly?: boolean;
  stripUnknown?: boolean;
  context?: Record<string, any>;
}

/**
 * Validates data against a Joi schema and returns the validated/sanitized result
 * Throws a HttpError if validation fails
 */
export function validateSchema<T = any>(
  data: any,
  schema: Joi.Schema,
  options: SchemaValidationOptions = {}
): T {
  const { error, value } = schema.validate(data, {
    abortEarly: options.abortEarly ?? false,
    stripUnknown: options.stripUnknown ?? true,
    context: options.context
  });

  if (error) {
    // Format validation errors
    const details = error.details.map(detail => ({
      message: detail.message,
      path: detail.path.join('.'),
      type: detail.type
    }));

    // Throw a validation error
    throw new HttpError(
      'Validation error',
      400,
      ErrorCode.VALIDATION_ERROR,
      details
    );
  }

  return value as T;
}

// /**
//  * Creates a validator function for a specific schema
//  */
// export function createValidator<T = any>(
//   schema: Joi.Schema,
//   options: SchemaValidationOptions = {}
// ): (data: any) => T {
//   return (data: any) => validateSchema<T>(data, schema, options);
// }

/**
 * Common schema builders for standard patterns
 */
export const schemaBuilders = {
  /**
   * Pagination schema with standard parameters
   */
  pagination: () => Joi.object({
    page: Joi.number().integer().min(1).default(1)
      .description('Page number (starting from 1)'),
    limit: Joi.number().integer().min(1).max(100).default(10)
      .description('Number of items per page'),
    sort: Joi.string().description('Sort field, prefix with - for descending order'),
    filter: Joi.string().description('Filter query')
  }),

  /**
   * UUID parameter schema
   */
  uuidParam: () => Joi.string().guid({ version: 'uuidv4' })
    .description('UUID v4 parameter'),

  /**
   * ID parameter schema (for common ID patterns)
   */
  idParam: (prefix?: string) => {
    let schema = Joi.string().min(3).max(50);

    if (prefix) {
      schema = schema.pattern(new RegExp(`^${prefix}_[a-zA-Z0-9]+$`))
        .description(`ID with prefix ${prefix}`);
    }

    return schema;
  }
};
